import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import {
  DownloadOutlined,
  ExportOutlined,
  FilePdfOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { isResumenCuentaImpresionEnabledForCoop } from "../../../utils/resumenCuentaImpresionAccess.js";

const { Title, Paragraph } = Typography;

const MESES = [
  "ENERO",
  "FEBRERO",
  "MARZO",
  "ABRIL",
  "MAYO",
  "JUNIO",
  "JULIO",
  "AGOSTO",
  "SEPTIEMBRE",
  "OCTUBRE",
  "NOVIEMBRE",
  "DICIEMBRE",
];

const PERIODOS_CANTIDAD = 12;

const buildPeriodos = () => {
  const periodos = [];
  const ahora = new Date();
  for (let i = 0; i < PERIODOS_CANTIDAD; i += 1) {
    const fecha = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1);
    const anio = fecha.getFullYear();
    const mesNumero = fecha.getMonth();
    const mesPad = String(mesNumero + 1).padStart(2, "0");
    periodos.push({
      value: `${anio}-${mesPad}-01`,
      label: `${MESES[mesNumero]} ${anio}`,
    });
  }
  return periodos;
};

const sanitizeCuenta = (raw) => String(raw || "").trim();

const CImprimirResumenCuenta = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfMeta, setPdfMeta] = useState(null);

  const periodos = useMemo(buildPeriodos, []);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || null;
    } catch {
      return null;
    }
  }, []);

  const idCooperativa = user?.user?.idCooperativa;
  const isEnabled = isResumenCuentaImpresionEnabledForCoop(idCooperativa);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const clearCurrentPdf = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setPdfMeta(null);
  };

  const handleGenerar = async (values) => {
    const cuenta = sanitizeCuenta(values.cuenta);
    const fecha = values.fecha;

    if (!cuenta) {
      message.warning("Ingresá un número de cuenta.");
      return;
    }
    if (!fecha) {
      message.warning("Seleccioná un período.");
      return;
    }

    setLoading(true);
    clearCurrentPdf();
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/resumen-cuenta/pdf`,
        {
          params: { cuenta, fecha },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfMeta({
        cuenta,
        fecha,
        label: periodos.find((p) => p.value === fecha)?.label || fecha,
        size: blob.size,
      });
      message.success("PDF generado correctamente.");
    } catch (error) {
      let serverMessage = "No se pudo generar el PDF.";
      try {
        if (error?.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          try {
            const json = JSON.parse(text);
            if (json?.message) serverMessage = json.message;
          } catch {
            if (text) serverMessage = text.slice(0, 200);
          }
        } else if (error?.response?.data?.message) {
          serverMessage = error.response.data.message;
        }
      } catch {
        // ignoramos: nos quedamos con el mensaje por defecto
      }
      console.error("Error al generar resumen de cuenta:", error);
      message.error(serverMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDescargar = () => {
    if (!pdfUrl || !pdfMeta) return;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `resumen_ctacte_${pdfMeta.cuenta}_${pdfMeta.fecha}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAbrirEnPestania = () => {
    if (!pdfUrl) return;
    const opened = window.open(pdfUrl, "_blank", "noopener,noreferrer");
    if (!opened) {
      message.error(
        "No se pudo abrir una pestaña nueva. Revisá el bloqueador de pop-ups."
      );
    }
  };

  // Imprimir es delicado cross-browser: en Chrome Windows muchas veces el
  // visor de PDF queda en otro origin y `iframe.contentWindow.print()` tira
  // SecurityError. Intentamos primero con un iframe oculto efímero y, si
  // falla, caemos a abrir el PDF en una pestaña nueva (donde el usuario
  // imprime con Ctrl+P en el visor nativo).
  const handleImprimir = () => {
    if (!pdfUrl) return;

    const printFrame = document.createElement("iframe");
    printFrame.style.position = "fixed";
    printFrame.style.right = "0";
    printFrame.style.bottom = "0";
    printFrame.style.width = "0";
    printFrame.style.height = "0";
    printFrame.style.border = "0";
    printFrame.style.visibility = "hidden";

    let triggered = false;
    let cleanedUp = false;
    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      setTimeout(() => {
        if (printFrame.parentNode) {
          printFrame.parentNode.removeChild(printFrame);
        }
      }, 1000);
    };

    const fallbackOpenInTab = (reason) => {
      console.warn("Fallback a pestaña nueva al imprimir:", reason);
      const opened = window.open(pdfUrl, "_blank", "noopener,noreferrer");
      if (opened) {
        message.info(
          "Abrimos el PDF en una pestaña nueva. Imprimí desde ahí con Ctrl+P."
        );
      } else {
        message.error(
          "No se pudo abrir el visor para imprimir. Probá descargarlo y abrirlo manualmente."
        );
      }
    };

    printFrame.onload = () => {
      if (triggered) return;
      triggered = true;
      setTimeout(() => {
        try {
          printFrame.contentWindow?.focus();
          printFrame.contentWindow?.print();
          // El iframe se limpia un rato después del print (cuando el usuario
          // vuelve al foco de la pestaña).
          const onFocus = () => {
            window.removeEventListener("focus", onFocus);
            cleanup();
          };
          window.addEventListener("focus", onFocus);
          setTimeout(cleanup, 60000);
        } catch (err) {
          cleanup();
          fallbackOpenInTab(err?.message || "SecurityError en iframe.print");
        }
      }, 250);
    };

    printFrame.onerror = () => {
      cleanup();
      fallbackOpenInTab("iframe onerror");
    };

    document.body.appendChild(printFrame);
    printFrame.src = pdfUrl;
  };

  if (!isEnabled) {
    return (
      <div style={{ padding: 16 }}>
        <Card title="Imprimir resumen de cuenta">
          <Paragraph>
            Esta funcionalidad no está habilitada para tu cooperativa.
          </Paragraph>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={
          <Space>
            <FilePdfOutlined />
            <span>Imprimir resumen de cuenta</span>
          </Space>
        }
      >
        <Paragraph type="secondary">
          Generá el mismo PDF de resumen de cuenta corriente que reciben los
          productores por WhatsApp. Ingresá el número de cuenta y el período a
          consultar.
        </Paragraph>

        <Form
          form={form}
          layout="vertical"
          initialValues={{ fecha: periodos[0]?.value }}
          onFinish={handleGenerar}
        >
          <Row gutter={16}>
            <Col xs={24} md={10}>
              <Form.Item
                label="Número de cuenta"
                name="cuenta"
                rules={[
                  { required: true, message: "Ingresá un número de cuenta." },
                  {
                    pattern: /^[A-Za-z0-9-]{1,32}$/,
                    message: "Solo letras, números y guiones (máx. 32).",
                  },
                ]}
              >
                <Input placeholder="Ej. 12345" allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item
                label="Período"
                name="fecha"
                rules={[{ required: true, message: "Seleccioná un período." }]}
              >
                <Select
                  placeholder="Seleccionar período"
                  options={periodos}
                  showSearch={false}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={4} style={{ display: "flex", alignItems: "end" }}>
              <Form.Item style={{ width: "100%" }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  icon={<FilePdfOutlined />}
                >
                  Generar PDF
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {pdfUrl && pdfMeta && (
          <Card
            type="inner"
            title={
              <Space>
                <span>Cuenta {pdfMeta.cuenta}</span>
                <span style={{ color: "#888" }}>• {pdfMeta.label}</span>
              </Space>
            }
            extra={
              <Space wrap>
                <Button icon={<ExportOutlined />} onClick={handleAbrirEnPestania}>
                  Abrir en pestaña
                </Button>
                <Button icon={<PrinterOutlined />} onClick={handleImprimir}>
                  Imprimir
                </Button>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDescargar}
                >
                  Descargar
                </Button>
              </Space>
            }
            style={{ marginTop: 16 }}
          >
            <object
              data={pdfUrl}
              type="application/pdf"
              aria-label="Previsualización del resumen de cuenta"
              style={{
                width: "100%",
                height: "75vh",
                border: "1px solid #f0f0f0",
                borderRadius: 4,
                backgroundColor: "#fafafa",
              }}
            >
              <div
                style={{
                  padding: 24,
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <FilePdfOutlined style={{ fontSize: 48, color: "#FB6B48" }} />
                <Paragraph style={{ margin: 0 }}>
                  Tu navegador no puede mostrar el PDF dentro de la página.
                </Paragraph>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  Igual podés abrirlo en una pestaña nueva o descargarlo desde
                  los botones de arriba.
                </Paragraph>
                <Space wrap>
                  <Button
                    icon={<ExportOutlined />}
                    onClick={handleAbrirEnPestania}
                  >
                    Abrir en pestaña
                  </Button>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleDescargar}
                  >
                    Descargar
                  </Button>
                </Space>
              </div>
            </object>
          </Card>
        )}

        {!pdfUrl && !loading && (
          <Title level={5} type="secondary" style={{ marginTop: 24 }}>
            El PDF se mostrará acá una vez generado.
          </Title>
        )}
      </Card>
    </div>
  );
};

export default CImprimirResumenCuenta;
